import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { formatApiErrorDetail } from "./api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public admin password-setup page. Landed here via a one-time setup URL
 * generated by the CLI (`reset_admin_password.py --generate-setup-link`)
 * or by an existing admin via /admin/settings/users. Requires no JWT.
 */
export default function SetupPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState({ status: "checking", email: "", msg: "" });
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) { setState({ status: "invalid", email: "", msg: "This setup link is missing its token." }); return; }
    axios.post(`${API}/admin/validate-setup-token`, { token })
      .then((r) => {
        if (r.data?.valid) setState({ status: "ok", email: r.data.email, msg: "" });
        else setState({ status: "invalid", email: "", msg: r.data?.reason || "This setup link is no longer valid." });
      })
      .catch((e) => setState({ status: "invalid", email: "", msg: formatApiErrorDetail(e.response?.data?.detail) || "Could not validate the setup link." }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw1 !== pw2) { setErr("The two passwords don't match."); return; }
    if (pw1.length < 12) { setErr("Use at least 12 characters."); return; }
    if (!/[A-Za-z]/.test(pw1) || !/[0-9]/.test(pw1)) { setErr("Include at least one letter and one number."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/consume-setup-token`, { token, new_password: pw1 });
      setState({ status: "done", email: state.email, msg: "" });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Could not set the password.");
    } finally { setBusy(false); }
  };

  return (
    <div className="ga-login-shell" data-testid="setup-page">
      <div className="ga-login-card" style={{ maxWidth: 480 }}>
        <div className="ga-eyebrow">Set your admin password</div>
        <h1 className="ga-login-title">Gold Petroleum admin</h1>

        {state.status === "checking" && <p className="ga-mute">Checking your setup link…</p>}

        {state.status === "invalid" && (
          <>
            <p className="ga-error" data-testid="setup-invalid">{state.msg}</p>
            <p className="ga-mute" style={{ marginTop: 16 }}>
              Ask your project owner or Emergent support to issue a fresh link.
              Setup links expire 24 hours after they're generated and can only be used once.
            </p>
            <Link to="/admin/login" className="ga-link" data-testid="setup-back-to-login">Back to sign-in</Link>
          </>
        )}

        {state.status === "ok" && (
          <form onSubmit={submit} className="ga-form" style={{ marginTop: 16 }}>
            <p className="ga-mute" data-testid="setup-email">Setting password for <strong>{state.email}</strong>.</p>
            <label className="ga-field ga-field--wide">
              <span>New password</span>
              <input type="password" name="new_password" required minLength={12} value={pw1} onChange={(e)=>setPw1(e.target.value)} data-testid="setup-pw1" />
            </label>
            <label className="ga-field ga-field--wide">
              <span>Confirm password</span>
              <input type="password" name="confirm_password" required minLength={12} value={pw2} onChange={(e)=>setPw2(e.target.value)} data-testid="setup-pw2" />
            </label>
            <p className="ga-mute" style={{ fontSize: 12 }}>Minimum 12 characters, with at least one letter and one number.</p>
            <div className="ga-savebar">
              <div>{err && <span className="ga-error" data-testid="setup-err">{err}</span>}</div>
              <button type="submit" className="ga-btn" disabled={busy} data-testid="setup-submit">
                {busy ? "Setting…" : "Set password"}
              </button>
            </div>
          </form>
        )}

        {state.status === "done" && (
          <>
            <p className="ga-saved" data-testid="setup-done">Password set. You can now sign in.</p>
            <button className="ga-btn" onClick={() => navigate("/admin/login")} data-testid="setup-go-login">Go to sign-in</button>
          </>
        )}
      </div>
    </div>
  );
}
